import React from 'react';

import { isObject } from 'utils/helpers/object';

import { toInlineString } from './helpers';
import RecommendationDynamicTable, { canRenderDynamicTableValue } from './RecommendationDynamicTable';
import RecommendationMarkdown from './RecommendationMarkdown';

const humanizeKey = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const objectToMarkdown = (value: Record<string, any>): string => {
  return Object.entries(value)
    .map(([key, val]) => `**${humanizeKey(key)}:** ${toInlineString(val)}`)
    .join('\n\n');
};

type Props = {
  value: unknown;
};

const InsightFieldRenderer = ({ value }: Props) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <RecommendationMarkdown>{String(value)}</RecommendationMarkdown>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }

    const isPrimitiveArray = value.every(
      (item) => item === null || ['string', 'number', 'boolean'].includes(typeof item)
    );

    if (isPrimitiveArray) {
      const markdownList = value.map((item) => `* ${String(item ?? '')}`).join('\n');
      return <RecommendationMarkdown>{markdownList}</RecommendationMarkdown>;
    }

    if (canRenderDynamicTableValue(value)) {
      return <RecommendationDynamicTable value={value} />;
    }

    return (
      <div>
        {value.map((item, index) => (
          <div key={index}>
            {isObject(item) ? (
              <RecommendationMarkdown>{objectToMarkdown(item)}</RecommendationMarkdown>
            ) : (
              <InsightFieldRenderer value={item} />
            )}
          </div>
        ))}
      </div>
    );
  }

  if (isObject(value)) {
    return <RecommendationMarkdown>{objectToMarkdown(value)}</RecommendationMarkdown>;
  }

  return <RecommendationMarkdown>{String(value)}</RecommendationMarkdown>;
};

export default InsightFieldRenderer;
